import React, { useState } from 'react'
import { AdCreative } from '@marketing-milk/interfaces'
import { CreativeMedia } from '../../creatives/CreativeCard/CreativeMedia'
import { useAdCreativeReview } from 'hooks/api/useAdCreativeReview'
import { StatusPill } from './StatusPill'
import { light, radius, shadow, status as statusColors, type as typo } from '../tokens'

type Props = {
  businessID: number
  creative: AdCreative
  /** Open the full-size preview on this ad (view mode). */
  onOpen: () => void
  /** Open the full-size preview on this ad with the reject-reason form already showing. */
  onOpenReject: () => void
}

/**
 * One ad in the client review grid: thumbnail, current status, and quick
 * Approve/Reject actions. Clicking the card (or its Reject button) opens the
 * full-size preview with the ad's copy/metadata alongside the same actions —
 * see `ReviewCreativePreviewModal`, rendered once by the parent page (not
 * here) so a successful decision there can advance straight into the next
 * card's ad instead of just closing. The quick Approve button below is a
 * separate, immediate action that never opens anything.
 */
export function ReviewCreativeCard({ businessID, creative, onOpen, onOpenReject }: Props) {
  const [hovered, setHovered] = useState(false)
  const { submitReview } = useAdCreativeReview(businessID, creative.id)

  const latestExternalReview = [...creative.reviews]
    .filter(review => review.type === 'external')
    .sort((a, b) => b.createdAt - a.createdAt)[0]

  const handleApprove = () => submitReview.mutate({ approved: true })

  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: `1px solid ${light.hairline}`,
        borderRadius: radius.lg,
        overflow: 'hidden',
        background: light.surface,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        boxShadow: hovered ? shadow.overlay : '0 1px 2px rgba(0,0,0,0.06)',
        transform: hovered ? 'translateY(-3px)' : 'none',
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
      }}
    >
      <div style={{ position: 'relative' }}>
        <CreativeMedia creative={creative} className="h-48 w-full bg-gray-100" />
        <div style={{ position: 'absolute', top: 10, left: 10 }}>
          <StatusPill creative={creative} />
        </div>
        {hovered && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.25)',
              color: '#fff',
              fontFamily: typo.family.sans,
              fontSize: typo.size.eyebrow,
              fontWeight: typo.weight.semibold,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            View ad
          </div>
        )}
      </div>

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div
          style={{
            fontFamily: typo.family.sans,
            fontWeight: typo.weight.semibold,
            fontSize: typo.size.bodyLg,
            color: light.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {creative.name}
        </div>

        {latestExternalReview && !latestExternalReview.approved && latestExternalReview.notes && (
          <div
            style={{
              fontFamily: typo.family.sans,
              fontSize: typo.size.body,
              color: light.textMuted,
              fontStyle: 'italic',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            “{latestExternalReview.notes}”
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            disabled={submitReview.isLoading}
            onClick={e => {
              e.stopPropagation()
              handleApprove()
            }}
            style={approveButtonStyle(submitReview.isLoading)}
          >
            Approve
          </button>
          <button
            type="button"
            disabled={submitReview.isLoading}
            onClick={e => {
              e.stopPropagation()
              onOpenReject()
            }}
            style={rejectButtonStyle(submitReview.isLoading)}
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  )
}

const baseButtonStyle: React.CSSProperties = {
  flex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: radius.sm,
  padding: '8px 12px',
  fontFamily: typo.family.sans,
  fontSize: typo.size.body,
  fontWeight: typo.weight.semibold,
  cursor: 'pointer',
}

const approveButtonStyle = (disabled: boolean): React.CSSProperties => ({
  ...baseButtonStyle,
  background: statusColors.success,
  border: `1px solid ${statusColors.success}`,
  color: '#fff',
  opacity: disabled ? 0.6 : 1,
  cursor: disabled ? 'default' : 'pointer',
})

const rejectButtonStyle = (disabled: boolean): React.CSSProperties => ({
  ...baseButtonStyle,
  background: 'transparent',
  border: `1px solid ${statusColors.danger}`,
  color: statusColors.danger,
  opacity: disabled ? 0.6 : 1,
  cursor: disabled ? 'default' : 'pointer',
})
